import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Hammer, Image as ImageIcon, Article, ChatCircle, EnvelopeSimple, Users, ArrowUpRight } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";

function Stat({ icon: Icon, label, value, to }) {
  return (
    <Link to={to} className="admin-card hover:border-atc-red transition-colors group" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <Icon size={24} weight="bold" className="text-atc-red" />
        <ArrowUpRight size={16} className="text-white/40 group-hover:text-white" />
      </div>
      <div className="mt-6 font-display text-5xl text-white leading-none">{value}</div>
      <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-white/50">{label}</div>
    </Link>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ services: 0, gallery: 0, blog: 0, testimonials: 0, contacts: 0, admins: 0 });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      const [svc, gal, blg, tst, msgs, admins] = await Promise.all([
        api.get("/services").catch(() => ({ data: [] })),
        api.get("/gallery").catch(() => ({ data: [] })),
        api.get("/blog?include_drafts=true").catch(() => ({ data: [] })),
        api.get("/testimonials").catch(() => ({ data: [] })),
        api.get("/admin/contacts").catch(() => ({ data: [] })),
        api.get("/admin/users").catch(() => ({ data: [] })),
      ]);
      setStats({
        services: svc.data.length,
        gallery: gal.data.length,
        blog: blg.data.length,
        testimonials: tst.data.length,
        contacts: msgs.data.length,
        admins: admins.data.length,
      });
      setRecent(msgs.data.slice(0, 5));
    })();
  }, []);

  return (
    <div className="p-8 md:p-10" data-testid="admin-dashboard">
      <div className="overline mb-3">Welcome back</div>
      <h1 className="font-display text-5xl uppercase text-white leading-none">Hey, {user?.name?.split(" ")[0] || "Admin"}.</h1>
      <p className="text-white/60 mt-3">Snapshot of ATC Home Pro's — {new Date().toLocaleDateString()}</p>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Stat icon={Hammer} label="Services" value={stats.services} to="/admin/services" />
        <Stat icon={ImageIcon} label="Gallery" value={stats.gallery} to="/admin/gallery" />
        <Stat icon={Article} label="Journal" value={stats.blog} to="/admin/blog" />
        <Stat icon={ChatCircle} label="Reviews" value={stats.testimonials} to="/admin/testimonials" />
        <Stat icon={EnvelopeSimple} label="Messages" value={stats.contacts} to="/admin/contacts" />
        <Stat icon={Users} label="Admins" value={stats.admins} to="/admin/users" />
      </div>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 admin-card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-2xl uppercase text-white">Recent messages</h2>
            <Link to="/admin/contacts" className="font-heading uppercase tracking-widest text-xs text-white/60 hover:text-atc-red">View all</Link>
          </div>
          {recent.length === 0 ? (
            <p className="text-white/50 text-sm">No messages yet.</p>
          ) : (
            <ul className="divide-y divide-[#2a2a2a]">
              {recent.map((m) => (
                <li key={m.id} className="py-4 flex items-start justify-between gap-4" data-testid={`recent-msg-${m.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {!m.read && <span className="bg-atc-red text-white text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5">New</span>}
                      <span className="font-heading uppercase text-sm text-white">{m.name}</span>
                      <span className="font-mono text-[10px] text-white/40">· {m.service || "General"}</span>
                    </div>
                    <p className="text-sm text-white/70 mt-1 line-clamp-2">{m.message}</p>
                  </div>
                  <div className="font-mono text-[10px] text-white/40 shrink-0">{new Date(m.created_at).toLocaleDateString()}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="admin-card">
          <h2 className="font-display text-2xl uppercase text-white mb-5">Quick actions</h2>
          <div className="space-y-2">
            <Link to="/admin/gallery" className="block border border-[#333] hover:border-atc-red p-4 group" data-testid="quick-upload-gallery">
              <div className="font-heading uppercase text-sm text-white">Upload to Gallery</div>
              <div className="font-mono text-[10px] text-white/40 mt-1 group-hover:text-atc-red">Add new project photos</div>
            </Link>
            <Link to="/admin/blog" className="block border border-[#333] hover:border-atc-red p-4 group" data-testid="quick-new-post">
              <div className="font-heading uppercase text-sm text-white">Write Journal Post</div>
              <div className="font-mono text-[10px] text-white/40 mt-1 group-hover:text-atc-red">Share a project update</div>
            </Link>
            <Link to="/admin/services" className="block border border-[#333] hover:border-atc-red p-4 group" data-testid="quick-services">
              <div className="font-heading uppercase text-sm text-white">Edit Services</div>
              <div className="font-mono text-[10px] text-white/40 mt-1 group-hover:text-atc-red">Update offerings & images</div>
            </Link>
            <Link to="/admin/settings" className="block border border-[#333] hover:border-atc-red p-4 group" data-testid="quick-settings">
              <div className="font-heading uppercase text-sm text-white">Site Settings</div>
              <div className="font-mono text-[10px] text-white/40 mt-1 group-hover:text-atc-red">Phone, email, hours, areas</div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
